'use client';

import { Fragment, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { loadTranslationMessages } from '../messages';
import { type LanguageProviderProps, defaultRichTextElements } from './shared';

/**
 * Holds `react-intl` for one locale's messages.
 *
 * The server resolves the set for the locale the tree renders in and passes it
 * as `initialMessages`, so the server render is already translated and the
 * browser has nothing to wait for. When the server sends a set for a new
 * locale, as `router.refresh()` does after Settings → Language, that set
 * replaces the active one. A `locale` with no server set loads its own.
 */
export function LanguageProvider({
  children,
  locale,
  initialMessages,
  timeZone,
}: LanguageProviderProps) {
  const [active, setActive] = useState(initialMessages);
  // Keyed on the locale, not the object: callers build `initialMessages`
  // inline, so its identity changes on every render.
  const [seededLocale, setSeededLocale] = useState(initialMessages.locale);
  if (initialMessages.locale !== seededLocale) {
    setSeededLocale(initialMessages.locale);
    if (initialMessages.locale === locale) {
      setActive(initialMessages);
    }
  }

  useEffect(() => {
    if (locale === active.locale) {
      return;
    }

    let cancelled = false;
    loadTranslationMessages(locale)
      .then((messages) => {
        if (!cancelled) {
          setActive({ locale, messages });
        }
      })
      .catch((error) => {
        console.error(`[i18n] Failed to load messages for ${locale}`, error);
      });

    return () => {
      cancelled = true;
    };
  }, [locale, active.locale]);

  function handleError(error: Error & { code: string }) {
    if (
      process.env.NEXT_PUBLIC_I18N_LOG_MISSING_TRANSLATIONS === 'true' &&
      error.code === 'MISSING_TRANSLATION'
    ) {
      console.warn(error.message);
    }
  }

  // Keyed on the locale whose messages are actually in hand: rendering a new
  // `locale` against the previous messages would blank every string for the
  // tick the new set takes to load.
  return (
    <IntlProvider
      locale={active.locale}
      key={active.locale}
      messages={active.messages}
      textComponent={Fragment}
      defaultRichTextElements={defaultRichTextElements}
      timeZone={timeZone}
      onError={handleError}
    >
      {children}
    </IntlProvider>
  );
}
